import { TWorkers } from '@consumption/data-access';
import { PLATFORM_FEATURES } from '@application/config';

export type TTimeSlot = {
  TimeSlotId: string;
  DayOfWeek: number;
  StartTime: string;
  EndTime: string;
};

export type TAccessTime = {
  AccessTimeId?: string;
  Name?: string;
  EarlyAccessMinutes?: number;
  LateAccessToleranceMinutes?: number;
  PreventEarlyExit?: boolean;
  TimeSlots?: TTimeSlot[];
};

export type TProduct = {
  ProductId: string;
  CategoryType: ECategoryType;
  Name: string;
  Icon: string | null;
  Workers: TWorkers[];
};

export type TCondition = {
  ConditionId: string;
  Name: string;
  InstallmentCount: number;
  DurationCount: number;
  DurationType: EPlanConditionDurationType;
};

export enum EPlanConditionDurationType {
  Day = 1,
  Month = 2,
}

export enum ECategoryType {
  Default = 0,
  Activity = 1,
  Good = 2,
  Service = 3,
  Module = 4,
}

export type TFinancialRule = {
  FinancialRuleId: string;
  FixedAmount: number | null;
  RatePerDay: number | null;
  AmountPerDay: number | null;
  ToleranceDays: number;
  MaxAmount: number | null;
  AppliesOffsetDays: number;
};

export type TAdjustmentRule = {
  FinancialAdjustmentId: string;
  Frequency: EPlanAdjustmentRuleFrequency;
  Amount: number;
  Rate: number;
  FinancialIndexId: string | null;
};

export enum EPlanAdjustmentRuleFrequency {
  None = 0,
  Daily = 1,
  Monthly = 2,
  Yearly = 3,
  Renewal = 4,
  Weekly = 5,
}

/**
 * Modos de base de cálculo da multa por encerramento antecipado (`TFinancialCancellationRule`).
 * Define sobre qual valor o percentual `Rate` (e `RatePerDay`) incide.
 */
export enum ECalculationBaseMode {
  /** Total das parcelas em aberto (fixo + variável). Padrão recomendado. */
  OpenTotal = 0,
  /** Apenas o valor fixo das parcelas em aberto (consumo ignorado). */
  OpenFixedOnly = 1,
  /** Total original do contrato, inclusive parcelas já recebidas. */
  OriginalTotal = 2,
}

/**
 * Regra de multa por encerramento antecipado de contrato.
 * Distinta de `TFinancialRule` (multa por atraso de pagamento, em `PenaltyRule`).
 *
 * Convenções:
 * - `Rate` é percent display (`10` = 10%) — backend persiste no mesmo formato que a UI exibe.
 * - `RatePerDay` é fração decimal (`0.10` = 10%) — legado, não exposto na UI.
 * - `MaxAmount` é estritamente > 0 quando informado; `null` significa "sem teto".
 * - Pelo menos UM de `FixedAmount`, `Rate`, `AmountPerDay`, `RatePerDay` deve ser > 0
 *   para a regra ser válida (servidor recusa caso contrário).
 * - `CalculationBaseMode` é obrigatório quando `Rate > 0`.
 */
export type TFinancialCancellationRule = {
  /** Identificador da regra. Omitir no POST de criação; obrigatório em update parcial. */
  FinancialCancellationRuleId?: string;
  /** Quantia fixa somada à multa. ≥ 0 quando informado. */
  FixedAmount: number | null;
  /** Percentual aplicado sobre o valor-base. Persistido como display percent (10 = 10%). */
  Rate: number | null;
  /** Modo de base de cálculo. Obrigatório quando Rate > 0. */
  CalculationBaseMode: ECalculationBaseMode | null;
  /** Quantia fixa multiplicada pelos dias restantes até o fim do contrato. ≥ 0. */
  AmountPerDay: number | null;
  /** Percentual por dia sobre o valor-base, em fração decimal. ≥ 0. */
  RatePerDay: number | null;
  /** Carência. Se faltarem ≤ N dias, multa é zero. Default 0. */
  ToleranceDays: number;
  /** Teto absoluto da multa bruta. **> 0** estrito quando informado; null = "sem teto". */
  MaxAmount: number | null;
};

export type TFinancialRuleSet = {
  FinancialRuleSetId: string;
  Name: string;
  DiscountRule: TFinancialRule;
  InterestRule: TFinancialRule;
  PenaltyRule: TFinancialRule;
  AdjustmentRule: TAdjustmentRule;
  /** Multa por encerramento antecipado (opcional). `null` quando não configurada. */
  CancellationRule: TFinancialCancellationRule | null;
};

export type TCycleFixedInstallment = {
  CycleFixedInstallmentId: string;
  InstallmentIndex: number;
  Amount: number;
};

export type TPlanCycle = {
  CycleId: string;
  PlanId: string;
  Name: string;
  /** Ciclo inativo não pode ser atribuído a novas assinaturas. */
  IsActive: boolean;
  IsGracePeriod?: boolean;
  InstallmentCount: number;
  DurationCount: number;
  DurationType: number | EPlanConditionDurationType;
  FixedInstallmentAmount: number;
  TrialDays: number;
  MaxCountPauses: number;
  MaxCountPausedDays: number;
  FixedInstallments: TCycleFixedInstallment[];
  AllowTrialDays?: boolean; //frontend only
  CommissionRate: number | null;
  CommissionAmount: number | null;
  AllowAccessLimit: boolean; //frontend only
  AccessLimit: number | null;
  ConsolidateAccessPerDay: boolean;
  AllowCommission?: boolean; //frontend only
  FinancialRuleSetId?: string | null;
  FinancialRuleSet?: TFinancialRuleSet | null;
  ContractModelId?: string | null;
  DependentType?: DependentType | null;
  MaxDependentCount?: number | null;
  AdditionalChargePerDependent?: number | null;
  /** Longest grace a launch may grant, in `MaxLaunchGraceUnit`. 0 = the cycle offers no grace. */
  MaxLaunchGraceCount: number;
  /** Unit of `MaxLaunchGraceCount`; null when the cycle offers no grace. */
  MaxLaunchGraceUnit: EPlanConditionDurationType | null;
  /** How many of the first installments a launch may exempt (0 to `InstallmentCount`). 0 = no exemption. */
  MaxLaunchExemptInstallments: number;
};

export enum EPlanResourceFrequencyType {
  None = 0,
  Daily = 1,
  Monthly = 2,
  Yearly = 3,
  Renewal = 4,
  Weekly = 5,
}

export type TPlanResource = {
  ResourceId: string;
  ProductId: string;
  Product: TProduct;
  FrequencyType: EPlanResourceFrequencyType;
  Count: number | null;
  AmountUnit: number | null;
  AllowExcess: boolean;
  ExceededAmountUnit: number | null;
  TrialDays: number;
  AllowAccumulate: boolean;
  AccessTimeId: string | null;
  AccessTime: TAccessTime;
  ResourcesRanges?: TResourcesRanges[];
};

export enum DependentType {
  Integrated = 1,
  Appendix = 2,
}

export type TTaxData = {
  TaxDataId?: string;
  Code?: string;
  TributaryType?: number;
  TributaryRequired?: number;
  TributaryRate?: number;
  TributaryCode?: string;
  Cnae?: string;
  NbsCode?: string;
};

export type TPlan = {
  PostPaid: boolean; //front-end only
  PlanId: string;
  Name: string;
  /** Plano inativo não pode ser atribuído a novas assinaturas. */
  IsActive: boolean;
  Resources: TPlanResource[];
  Cycles: TPlanCycle[];
  FinancialRuleSetId: string | null;
  FinancialRuleSet?: TFinancialRuleSet;
  PlansAccessesTimes?: TPlanAccessTime[];
  TaxData?: TTaxData;
  /** De onde o plano veio. `Cutover` = migrado do legado. */
  Origin?: EPlanOrigin | keyof typeof EPlanOrigin | null;
};

/**
 * Origem do plano no OData de Planos. `Cutover` marca o plano migrado do
 * sistema legado, que não pode sofrer alteração alguma.
 */
export enum EPlanOrigin {
  Cutover = 1,
}

/**
 * O OData serializa enum pelo nome (`'Cutover'`) e a API REST pelo número
 * (`1`); aceita os dois.
 */
export const isLegacyPlan = (
  origin: TPlan['Origin'] | undefined
): boolean =>
  origin === EPlanOrigin.Cutover ||
  origin === EPlanOrigin[EPlanOrigin.Cutover];

export type TPartialPlan = {
  PlanId: string;
  Name: string;
  /** Plano inativo não pode ser atribuído a novas assinaturas. */
  IsActive: boolean;
};

export type TResourcesRanges = {
  ResourceRangeId?: string;
  Index: number;
  StartRange: number;
  /** `null` na última faixa, quando ela não tem teto. */
  EndRange: number | null;
  Amount: number;
  /**
   * The tier is offered as a floor at launch, its `EndRange` becoming the `PlanMinimumQuantity`.
   * The backend only stores it: it forces `false` outside the active subscriptions resource and
   * filters nothing by it, so every rule that depends on it lives in the front.
   */
  OfferAsFloor?: boolean;
};

export enum EConsumptionType {
  Unitary,
  Ranges,
}

export type TPlanAccessTime = {
  PlanAccessTimeId?: string;
  PlanId?: string;
  AccessTimeId?: string;
  AccessTime?: TAccessTime;
  /**
   * Vagas do horário **neste plano** (o mesmo horário em outro plano tem a
   * própria cota). `null` = sem limite; quando informado, inteiro ≥ 1.
   * Para remover o limite, mande `null` no PUT do plano — o PATCH ignora `null`.
   */
  MaxSubscriptions?: number | null;
};

/**
 * Ocupação de um horário do plano (`GET /Plan/GetAccessTimesOccupancy`).
 * Cada assinatura conta 1 pelo titular + 1 por dependente ativo.
 */
export type TPlanAccessTimeOccupancy = {
  PlanAccessTimeId: string;
  AccessTimeId: string;
  AccessTimeName: string;
  /** `null` = sem limite. */
  MaxSubscriptions: number | null;
  Occupied: number;
  /** `null` quando não há limite; nunca negativo (o limite pode estar abaixo da ocupação). */
  Available: number | null;
};

export enum ESubscriptionIds {
  PLAN = 'plan',
  PACKS = 'packs',
  SPECIAL_CONDITIONS = 'special-conditions-section',
  FINANCIAL_RULE_SETS = 'financial-rule-sets-section',
  ACCESS_TIME = 'access-time-section',
  RENEWAL = 'renewal-section',
}

export const subscriptionsPageFragments = [
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.SUBSCRIPTION_CONFIG',
    id: ESubscriptionIds.PLAN,
  },
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.PACKS',
    id: ESubscriptionIds.PACKS,
  },
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.SPECIAL_CONDITIONS',
    id: ESubscriptionIds.SPECIAL_CONDITIONS,
  },
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.FINANCIAL_RULE_SETS',
    id: ESubscriptionIds.FINANCIAL_RULE_SETS,
  },
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.RENEWAL',
    id: ESubscriptionIds.RENEWAL,
  },
  {
    title: 'SETTINGS.SUBSCRIPTIONS.SUMMARY.ACCESS_TIME',
    id: ESubscriptionIds.ACCESS_TIME,
    hiddenPlatformFeatures: [PLATFORM_FEATURES.school],
  },
];
